import { useQueryClient } from "@tanstack/react-query";
import {
  AtSign, BadgeCheck, BookUser, Building2, ChevronDown, Mail, Webhook, ClipboardList, Disc3, Grid3x3, MessageSquare, Contact, Headphones, History, KeyRound, LogOut, Menu, Phone, Shield, ShieldCheck, Users, Users2, Voicemail, X, Globe,
} from "lucide-react";
import { useCallback, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { post } from "../api/client";
import type { Session } from "../api/types";
import { useNewVoicemails } from "../features/voicemail/useNewVoicemails";
import { missedCallsKey, useMissedCalls, useRefreshBadgeOnChange, useUnreadMessages } from "./useBadgeCounts";
import { useActiveCalls } from "../voice/useActiveCalls";
import { useEmailLive, useEmailUnread } from "../features/email/useEmailLive";
import { useVoice } from "../voice/store";
import { useCan, useSession, useSetSession } from "../auth/session";
import { toast } from "../components/ui/toast";
import { errorMessage } from "../lib/format";
import { useMediaQuery } from "../lib/useMediaQuery";
import { NotificationsPrompt, NotificationsWatcher } from "../features/notifications/NotificationsWatcher";
import { IncomingCallOverlay } from "../features/softphone/IncomingCallOverlay";
import { Softphone } from "../features/softphone/Softphone";
import { voice } from "../voice/manager";
import { VoiceBootstrap } from "../voice/VoiceBootstrap";
import { DeviceStatusBadge } from "../features/softphone/DeviceStatusBadge";

type NavItem = { to: string; label: string; icon: ComponentType<{ className?: string }>; show: boolean; count?: number; countLabel?: string };

export function AppShell() {
  const { data: session } = useSession();
  const { can, canAny, hasPlatformAccess } = useCan();
  const [navOpen, setNavOpen] = useState(false);
  const wide = useMediaQuery("(min-width: 1024px)");
  const canVoicemail = canAny("voicemail.read", "voicemail.read.organization");
  const newVoicemails = useNewVoicemails(canVoicemail);
  const canHistory = canAny("calls.read.own", "calls.read.team", "calls.read.organization");
  const canMessages = canAny("messages.read.own", "messages.read.team", "messages.read.organization");
  const missedCalls = useMissedCalls(canHistory);
  const unreadMessages = useUnreadMessages(canMessages);
  const { data: liveCalls } = useActiveCalls();
  useRefreshBadgeOnChange(liveCalls, missedCallsKey); // a call that rang here just ended
  const navigate = useNavigate();
  const canEmail = can("email.use");
  const emailUnread = useEmailUnread(canEmail);
  const openEmail = useCallback((_mailboxId: string, emailId: string) => navigate(`/email?email=${emailId}`), [navigate]);
  useEmailLive(canEmail, openEmail);
  // Pages that need the whole width (Email) hide the softphone panel, except during a call.
  const { pathname } = useLocation();
  const fullWidthPage = pathname === "/email" || pathname.startsWith("/email/");
  const inCall = useVoice((v) => !!v.activeCallSid || v.sdkCallState !== null || v.incoming.length > 0);

  const main: NavItem[] = [
    { to: "/phone", label: "Dialer", icon: Grid3x3, show: true },
    { to: "/history", label: "Call history", icon: History, show: canHistory, count: missedCalls.data?.unseen, countLabel: "missed" },
    { to: "/messages", label: "Messages", icon: MessageSquare, show: canMessages, count: unreadMessages.data?.conversations, countLabel: "unread" },
    { to: "/voicemail", label: "Voicemail", icon: Voicemail, show: canVoicemail, count: newVoicemails.data?.unheard },
    { to: "/recordings", label: "Recordings", icon: Disc3, show: canAny("recordings.read.own", "recordings.read.team", "recordings.read.organization") },
    { to: "/contacts", label: "Contacts", icon: Contact, show: can("contacts.read") },
    { to: "/settings/audio", label: "Audio settings", icon: Headphones, show: canAny("calls.initiate", "calls.receive") },
  ];
  const admin: NavItem[] = [
    { to: "/admin/organization", label: "Organization", icon: Building2, show: can("organizations.read") },
    { to: "/admin/members", label: "Members", icon: Users, show: can("users.read") },
    { to: "/admin/roles", label: "Roles", icon: ShieldCheck, show: can("roles.read") },
    { to: "/admin/teams", label: "Teams", icon: Users2, show: can("teams.read") },
    { to: "/admin/phone-numbers", label: "Phone numbers", icon: Phone, show: can("phone_numbers.read") },
    { to: "/admin/mailboxes", label: "Mailboxes", icon: AtSign, show: can("email.mailboxes.manage") },
    { to: "/admin/webhooks", label: "Webhooks", icon: Webhook, show: can("webhooks.manage") },
    { to: "/admin/audit", label: "Audit log", icon: ClipboardList, show: can("audit.read") },
  ];
  const platform: NavItem[] = [{ to: "/platform", label: "Platform", icon: Globe, show: hasPlatformAccess }];

  const nav = (
    <nav aria-label="Main" className="flex h-full flex-col gap-6 overflow-y-auto p-3">
      <NavSection label="Phone" icon={Phone} storageKey="nav.phone-open" items={main} onNavigate={() => setNavOpen(false)} />
      <NavGroup items={[
        { to: "/email", label: "Email", icon: Mail, show: canEmail, count: emailUnread.data?.unread, countLabel: "unread" },
        { to: "/address-book", label: "Address book", icon: BookUser, show: canEmail },
        { to: "/brands", label: "Brands", icon: BadgeCheck, show: can("brands.read") },
      ]} onNavigate={() => setNavOpen(false)} />
      {admin.some((i) => i.show) && <NavGroup title="Administration" items={admin} onNavigate={() => setNavOpen(false)} />}
      {platform.some((i) => i.show) && <NavGroup title="Platform" items={platform} onNavigate={() => setNavOpen(false)} />}
      <div className="mt-auto">
        <NavGroup items={[{ to: "/account", label: "Account & security", icon: KeyRound, show: true }]} onNavigate={() => setNavOpen(false)} />
      </div>
    </nav>
  );

  return (
    <div className="flex h-full flex-col gap-3 p-2 sm:p-3">
      <VoiceBootstrap />
      <NotificationsWatcher />
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2">Skip to content</a>
      <header className="glass flex h-14 shrink-0 items-center gap-3 rounded-2xl px-3">
        <button className="focus-ring rounded-xl p-2 text-muted hover:bg-white/70 lg:hidden" aria-label={navOpen ? "Close menu" : "Open menu"} aria-expanded={navOpen} onClick={() => setNavOpen((o) => !o)}>
          {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <NavLink to="/phone" className="focus-ring flex shrink-0 items-center rounded-lg" aria-label="AtlanTech Solution home">
          <img src="/brand/atlantech/logo.svg" alt="AtlanTech Solution" className="hidden h-9 w-auto sm:block" />
          <img src="/brand/atlantech/icon.svg" alt="AtlanTech Solution" className="h-9 w-9 sm:hidden" />
        </NavLink>
        {session && <OrgSwitcher session={session} />}
        <div className="ml-auto flex items-center gap-3">
          <DeviceStatusBadge />
          <UserMenu />
        </div>
      </header>
      <NotificationsPrompt />
      <div className="flex min-h-0 flex-1 gap-3">
        <aside className="glass hidden w-60 shrink-0 rounded-2xl lg:block">{nav}</aside>
        {navOpen && (
          <div className="fixed inset-0 z-30 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
            <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-md" onClick={() => setNavOpen(false)} />
            <aside className="glass absolute inset-y-2 left-2 w-64 animate-float-in rounded-2xl">{nav}</aside>
          </div>
        )}
        <main id="main" className="min-w-0 flex-1 overflow-y-auto rounded-2xl p-2 sm:p-4">
          <Outlet />
        </main>
        {wide && canAny("calls.initiate", "calls.receive") && (!fullWidthPage || inCall) && (
          <aside aria-label="Softphone" className="glass w-[360px] shrink-0 overflow-y-auto rounded-2xl">
            <Softphone />
          </aside>
        )}
      </div>
      <IncomingCallOverlay />
    </div>
  );
}

function NavItemLink({ item: { to, label, icon: Icon, count, countLabel = "new" }, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        `focus-ring flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm transition-all ${isActive ? "bg-accent-gradient font-medium text-white shadow-glow" : "text-muted hover:bg-white/70 hover:text-default"}`
      }
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      {!!count && <CountBadge count={count} label={countLabel} />}
    </NavLink>
  );
}

function CountBadge({ count, label }: { count: number; label: string }) {
  return (
    <span className="grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white" aria-label={`${count} ${label}`}>
      {count > 99 ? "99+" : count}
    </span>
  );
}

function NavGroup({ title, items, onNavigate }: { title?: string; items: NavItem[]; onNavigate: () => void }) {
  const visible = items.filter((i) => i.show);
  if (!visible.length) return null;
  return (
    <div>
      {title && <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-subtle">{title}</p>}
      <ul className="space-y-0.5">
        {visible.map((item) => <li key={item.to}><NavItemLink item={item} onNavigate={onNavigate} /></li>)}
      </ul>
    </div>
  );
}

const readOpen = (key: string) => { try { return localStorage.getItem(key) !== "closed"; } catch { return true; } };
const writeOpen = (key: string, open: boolean) => { try { localStorage.setItem(key, open ? "open" : "closed"); } catch { /* remembered for this page only */ } };

/**
 * A collapsible menu group. Closed, its header shows the combined count of its items' badges, and is
 * highlighted when you're on one of its pages. The open/closed choice is remembered in this browser.
 */
function NavSection({ label, icon: Icon, items, storageKey, onNavigate }: {
  label: string; icon: ComponentType<{ className?: string }>; items: NavItem[]; storageKey: string; onNavigate: () => void;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const visible = items.filter((i) => i.show);
  if (!visible.length) return null;
  const here = visible.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  const total = visible.reduce((n, i) => n + (i.count ?? 0), 0);
  const listId = `nav-${storageKey.replace(/\W/g, "-")}`;
  const toggle = () => setOpen((o) => { writeOpen(storageKey, !o); return !o; });
  return (
    <div>
      <button
        type="button" onClick={toggle} aria-expanded={open} aria-controls={listId}
        className={`focus-ring flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold transition-all hover:bg-white/70 ${!open && here ? "bg-accent-500/10 text-accent-700" : "text-default"}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="flex-1 text-left">{label}</span>
        {!open && total > 0 && <CountBadge count={total} label="new" />}
        <ChevronDown className={`h-4 w-4 text-subtle transition-transform ${open ? "" : "-rotate-90"}`} aria-hidden="true" />
      </button>
      {open && (
        <ul id={listId} aria-label={label} className="ml-5 mt-0.5 space-y-0.5 border-l border-default pl-2">
          {visible.map((item) => <li key={item.to}><NavItemLink item={item} onNavigate={onNavigate} /></li>)}
        </ul>
      )}
    </div>
  );
}

function OrgSwitcher({ session }: { session: Session }) {
  const setSession = useSetSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  if (session.memberships.length <= 1) {
    return session.organization ? <span className="hidden truncate text-sm text-muted md:inline">{session.organization.name}</span> : null;
  }
  return (
    <label className="hidden items-center gap-2 text-sm md:flex">
      <Shield className="h-4 w-4 text-subtle" aria-hidden="true" />
      <span className="sr-only">Organization</span>
      <select
        className="focus-ring h-9 rounded-xl border border-white/70 bg-white/60 px-2 text-sm text-default backdrop-blur"
        value={session.membershipId ?? ""}
        onChange={async (e) => {
          try {
            const next = await post<Session>("/auth/session/organization", { membershipId: e.target.value });
            setSession(next);
            qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
            navigate("/phone");
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      >
        {session.memberships.map((m) => (
          <option key={m.membershipId} value={m.membershipId}>{m.organizationName}</option>
        ))}
      </select>
    </label>
  );
}

function UserMenu() {
  const { data: session } = useSession();
  const setSession = useSetSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const logout = async () => {
    setBusy(true);
    try {
      await voice.stop();
      await post("/auth/logout");
    } catch {
      // Even if the request fails the local session state is cleared below.
    } finally {
      setSession(null);
      qc.clear();
      navigate("/login", { replace: true });
    }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="hidden text-sm text-muted sm:inline">{session?.user.name}</span>
      <button className="focus-ring inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-sm text-muted transition hover:bg-white/70 hover:text-default" onClick={logout} disabled={busy}>
        <LogOut className="h-4 w-4" aria-hidden="true" /> <span className="hidden sm:inline">Sign out</span>
      </button>
    </div>
  );
}
